/**
 * Copyright (c) Microsoft. All rights reserved.
 */

/** Wrapper to send request with specified timeout */
export function fetchWithTimeout(request: Request, timeoutMs: number): Promise<Response> {
    let timeoutId: Timeout | undefined;
    const fetchPromise = new Promise<Response>((resolve, reject) => {
        fetch(request).then((response) => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
            resolve(response);
        }).catch((error) => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
            reject(error);
        });
    });
    if (timeoutMs !== 0) {
        const timeoutPromise = new Promise<Response>((resolve, reject) => {
            timeoutId = setTimeout(reject, timeoutMs, new Error(`Fetch for '${request.url}' timed out`));
          });

        return Promise.race([fetchPromise, timeoutPromise]);
    }

    return fetchPromise;
}

export function toJson(obj: unknown): string {
    try {
        return JSON.stringify(obj);
    } catch (error) {
        return `Unable to serialize object of type ${typeof obj}`;
    }
}

/**
 * Simplify calculation of time intervals
 */
export class Timespan {
    private start: number;

    constructor() {
        this.start = Date.now();
    }

    /**
     * Returns number of milliseconds from start
     */
    public get duration(): number {
        return Date.now() - this.start;
    }

    /**
     * Returns start time
     */
    public get startTime() {
        return this.start;
    }

    /**
     * Reset start of interval to current timestamp
     */
    public reset() {
        this.start = Date.now();
    }
}

// Browser vs. @types/node compatibility - browser (where the client is mainly used) uses `number` as return type of setTimeout,
// node.js (where the tests run) uses its own type.
export type Timeout = ReturnType<typeof setTimeout>;



// WEBPACK FOOTER //
// ./lib_temp/Common.ts